
/* The research and race modifiers the server applies, for the screens that quote the numbers.

   `simulator/use_cases/modifiers.py` is a set of pure lookups against a player's researches and
   race, and every use case reads them before charging or reporting anything. The UI mostly did
   not, so a player who bought a bonus saw the un-bonused number in front of them:

     - the production panel priced units at list price, ignoring Military technology
     - the building modal said a wall absorbs `HEALTH_PER_LEVEL * level`, ignoring Impenetrable
     - `upgradeCostFor` charged `A * level + B`, ignoring the Infestor's -25% building price

   None of them blocked anything — the server does the real arithmetic — but each states a number
   that is not what the game does, which is requirement 17's whole subject. Mirroring the helpers
   here keeps one shape for all of them; the constants themselves come from `GET /game-config/`, so
   no balance number is written twice.

   Each function takes the same bag as the server's does — team, race, research levels — and
   returns a plain multiplier, so callers compose them the way the use cases do. */

const raceBonus = ({ gameConfig, race, key }) =>
  gameConfig?.rules_config?.RACE_BONUSES?.[race]?.[key] ?? 0

const level = (researchLevels, key) => researchLevels?.[key] || 0

/* The actions a round allows: `MAX_ACTIONS_PER_ROUND` plus the race's flat `ACTIONS` (the
   Intelligent zombie's "+2 actions per round") — `modifiers.max_actions`. */
export const maxActions = ({ gameConfig, race }) =>
  (gameConfig?.rules_config?.MAX_ACTIONS_PER_ROUND ?? 0) + Math.trunc(raceBonus({ gameConfig, race, key: "ACTIONS" }))

/* Humans only — the zombie half of this pair buys food from kills instead. Each level takes the
   reduction off what the last one left (owner, 2026-09-28): 1 -> 0.9 -> 0.81, so it never reaches
   zero and needs no floor — `modifiers.unit_cost_multiplier`. */
export const unitCostMultiplier = ({ gameConfig, team, researchLevels }) => {
  if (team !== "human") return 1
  const reduction = gameConfig?.rules_config?.RESEARCH_COST_REDUCTION ?? 0
  return (1 - reduction) ** level(researchLevels, "military_technology")
}

/* What an order whose list price is `listPrice` costs — `modifiers.discounted_worker_cost`. The
   discount comes off a city's whole order, never off each body (owner, 2026-09-14), so 1,000
   soldiers at one level cost 900 where a per-body price rounded every soldier back up to 1. Rounded
   up, so a small order is never free; rounded to six places first, because 0.9 ** 3 is
   0.7290000000000001 and 1,000 × that would otherwise quote 730. */
export const discountedOrderCost = ({ listPrice, gameConfig, team, researchLevels }) =>
  Math.ceil(Number(((listPrice || 0) * unitCostMultiplier({ gameConfig, team, researchLevels })).toFixed(6)))

/* One body's share of an order, after the discount — fractional on purpose (a soldier is 0.9 of a
   worker at one level), because the order is what gets rounded, not the body. */
export const discountedUnitCost = ({ listPrice, gameConfig, team, researchLevels }) =>
  Number(((listPrice || 0) * unitCostMultiplier({ gameConfig, team, researchLevels })).toFixed(6))

/* Humans only — zombies have no wall. */
export const wallHealthMultiplier = ({ gameConfig, team, researchLevels }) => {
  if (team !== "human") return 1
  const bonus = gameConfig?.rules_config?.RESEARCH_WALL_BONUS ?? 0
  return 1 + bonus * level(researchLevels, "impenetrable")
}

/* The Infestor's "-25% building price". Floored so a bonus can never make upgrades free. */
export const buildingCostMultiplier = ({ gameConfig, race }) =>
  Math.max(0.1, 1 + raceBonus({ gameConfig, race, key: "BUILDING_COST" }))

/* Cultivation Centre / Putrefactor output: the irrigation-or-rot-control research line, plus the
   Economist's "+50% food production" and the Infestor's "+25% meat production". */
export const productionMultiplier = ({ gameConfig, team, race, researchLevels }) => {
  const researchKey = team === "human" ? "irrigation" : "rot_control"
  const bonus = gameConfig?.rules_config?.RESEARCH_PRODUCTION_BONUS ?? 0
  return (
    1 + bonus * level(researchLevels, researchKey) + raceBonus({ gameConfig, race, key: "PRODUCTION" })
  )
}

/* Hatchery production capacity (Outbreak). The horde's alone since 2026-09-29: Prosperity moved
   off the command centre's worker ceiling onto its growth (`populationGrowthMultiplier`). */
export const capacityMultiplier = ({ gameConfig, team, researchLevels }) => {
  if (team === "human") return 1
  const bonus = gameConfig?.rules_config?.RESEARCH_CAPACITY_BONUS ?? 0
  return 1 + bonus * level(researchLevels, "outbreak")
}

/* Workers added per command centre level per tick: Prosperity's +15% a level plus the Economist's
   "+50% population growth", summed like `population_growth_multiplier` on the server. */
export const populationGrowthMultiplier = ({ gameConfig, team, race, researchLevels }) => {
  const bonus = team === "human" ? gameConfig?.rules_config?.RESEARCH_POPULATION_GROWTH_BONUS ?? 0 : 0
  return Math.max(
    0,
    1 + bonus * level(researchLevels, "prosperity") + raceBonus({ gameConfig, race, key: "POPULATION_GROWTH" })
  )
}

/* The Warrior's "-25% food consumption". No research touches upkeep, so race is the only input. */
export const consumptionMultiplier = ({ gameConfig, race }) =>
  Math.max(0, 1 + raceBonus({ gameConfig, race, key: "CONSUMPTION" }))

/* The Technologist's and the Intelligent's "+25% Investigation Points". A race bonus and nothing
   else — no research scales it — which is why the city modal may quote a research rate for a city
   it does not own: a player's race is public, their researches are not. */
export const researchPointMultiplier = ({ gameConfig, race }) =>
  Math.max(0, 1 + raceBonus({ gameConfig, race, key: "RESEARCH_POINTS" }))

/* Cells a stack's allowance pays for in a round — `modifiers.march_pace`. Roads / Microbiotic
   connection add `ROADS_SPEED_BONUS` (10%) of the BASE speed a level, linear, floored before it is
   walked (owner, 2026-09-29): speed 5 is 5 at level 1 and 6 at level 2, speed 10 is 11 at level 1.
   A race's `MOVEMENT` stays a flat cell on top. Rounded before the floor against float noise, as
   the server does. */
export const marchPace = ({ baseSpeed, gameConfig, team, race, researchLevels }) => {
  const researchKey = team === "human" ? "roads" : "microbiotic_connection"
  const bonus = gameConfig?.rules_config?.ROADS_SPEED_BONUS ?? 0
  const raised = baseSpeed * (1 + bonus * level(researchLevels, researchKey))
  const floored = Math.floor(Math.round(raised * 1e6) / 1e6)
  return floored + Math.trunc(raceBonus({ gameConfig, race, key: "MOVEMENT" }))
}

/* The Technologist's "+100% missiles". */
export const missileDamageMultiplier = ({ gameConfig, race }) =>
  Math.max(0, 1 + raceBonus({ gameConfig, race, key: "MISSILE_DAMAGE" }))

/* The silo's reach and cadence. The building's level buys damage only (S13, 2026-09-05); reach
   comes from research, and that research is **the building's own programme** — the first level
   turns the building on, and every level after it is `RESEARCH_RANGE_BONUS` cells of reach (owner's
   U6, 2026-09-07, the programme and `balistica` grouped). The reload is `STRIKE_COOLDOWN_TICKS`
   always and nothing shortens it. Mirrors `modifiers.silo_range`, `silo_cooldown`.

   `level` is only read on an old payload that still publishes `STRIKE_RADIUS_PER_LEVEL` (reach was
   level × radius then), so a client ahead of its server quotes what that server will do. */
const rangeBonus = ({ gameConfig, researchLevels, key, pastTheFirst = false }) =>
  (gameConfig?.rules_config?.RESEARCH_RANGE_BONUS ?? 0) *
  Math.max(0, level(researchLevels, key) - (pastTheFirst ? 1 : 0))

export const siloRange = ({ gameConfig, researchLevels, level: siloLevel = 1 }) => {
  const silo = gameConfig?.human_config?.BUILDINGS?.missile_silo || {}
  if (silo.STRIKE_RADIUS_BASE == null && silo.STRIKE_RADIUS_PER_LEVEL != null) {
    return silo.STRIKE_RADIUS_PER_LEVEL * (siloLevel || 0)
  }
  return (
    (silo.STRIKE_RADIUS_BASE ?? 0) +
    rangeBonus({ gameConfig, researchLevels, key: "missile_program", pastTheFirst: true })
  )
}

/* Always the configured reload (U6): the player is taken so every caller quotes the cadence
   through the same door as the reach, and a rule that one day scales it again changes one place. */
export const siloCooldown = ({ gameConfig }) =>
  gameConfig?.human_config?.BUILDINGS?.missile_silo?.STRIKE_COOLDOWN_TICKS ?? 0

/* The tunnel (owner, 2026-09-29; it was the Red Nydus's jump, with a reach and a reopening that
   both went). Two numbers, both doubling (owner, 2026-09-30), mirroring
   `use_cases/tunnel.load_capacity` and `modifiers.tunnel_network_capacity`:

   - how many bodies one ENTRY through a tunnel of this level carries — the ENTERING city's
     level: `LOAD_CAPACITY_BASE × LOAD_CAPACITY_GROWTH ** (level - 1)`, 100, 200, 400, 800;
   - how many the player's one network may hold — the tunnel program's level:
     `TUNNEL_NETWORK_BASE × TUNNEL_NETWORK_GROWTH ** (level - 1)`, 100, 200, 400, 800, 1600.

   Level 0 is 0 for both (no building; the locked program). Entering and leaving are each an
   ordinary action and have no number here. */
const doubling = (base, growth, atLevel) => (atLevel > 0 && base ? base * growth ** (atLevel - 1) : 0)

export const tunnelLoadCapacity = ({ gameConfig, level: tunnelLevel = 0 }) => {
  const tunnel = gameConfig?.zombie_config?.BUILDINGS?.tunnel || {}
  return doubling(tunnel.LOAD_CAPACITY_BASE ?? 0, tunnel.LOAD_CAPACITY_GROWTH ?? 2, tunnelLevel)
}

export const tunnelNetworkCapacity = ({ gameConfig, researchLevels }) => {
  const rules = gameConfig?.rules_config || {}
  return doubling(rules.TUNNEL_NETWORK_BASE ?? 0, rules.TUNNEL_NETWORK_GROWTH ?? 2, level(researchLevels, "tunnel_program"))
}

/* Bodies the network holds now, every type together. */
export const tunnelContents = (tunnel) =>
  Object.values(tunnel || {}).reduce((total, unit) => total + (Number(unit?.number) || 0), 0)

/* What the next level of a research costs, the server's way (`apply_research.research_cost`):
   `RESEARCH_COST_BASE` for the first level and `RESEARCH_COST_GROWTH` times the previous one for
   each after it — 100, 200, 400, ... (owner, 2026-09-28). The two special-building programs open
   at `RESEARCH_PROGRAM_COST_BASE` instead — 800, 1,600, ... (owner, 2026-10-09). One helper, so the
   panel and the catalogue selector cannot quote two prices for one line. */
export const PROGRAM_RESEARCHES = ["missile_program", "tunnel_program"]
export const researchPrice = ({ rules, level: current = 0, research }) => {
  const base = PROGRAM_RESEARCHES.includes(research)
    ? rules?.RESEARCH_PROGRAM_COST_BASE ?? rules?.RESEARCH_COST_BASE ?? 0
    : rules?.RESEARCH_COST_BASE ?? 0
  return base * (rules?.RESEARCH_COST_GROWTH ?? 1) ** Math.max(0, current)
}

/* Points one Academy / Evolution Chamber of this level earns in a round, before the race bonus —
   the server's `apply_research.research_output`: `RESEARCH_PER_LEVEL × RESEARCH_OUTPUT_GROWTH **
   level`, so 50, 100, 200, 400, ... Each level earns double the last (owner, 2026-09-29), because
   each research level costs double the last. Every screen that quotes a research income reads
   this, never `RESEARCH_PER_LEVEL` alone. */
export const researchOutput = ({ rules, level: buildingLevel = 0 }) => {
  const n = Math.trunc(buildingLevel || 0)
  if (n <= 0) return 0
  return (rules?.RESEARCH_PER_LEVEL ?? 0) * (rules?.RESEARCH_OUTPUT_GROWTH ?? 2) ** n
}
